<script setup lang="ts">
import CloseBoldIcon from '~/assets/icon/close-2.svg'
import { uploadWebImage, type WebUploadImagePayload } from '~/server/common'
import { submitFeedbackImmediate, getPostFeedbackSetting, validateFeedbackGeetest } from '~/server/exchange-detail'
import FankuiIcon from '~/assets/icon/fankui.svg'
import StarIcon from '~/assets/icon/star.svg'
import CloseIcon from '~/assets/icon/close.svg'
import type { TraderListItem } from '~/types/home'
const {
  show,
  traderCode = '',
  commentGradeId = '',
  exchangeSlug = '',
  feedbackShowType = '',
  feedbackLogo = '',
  feedbackName = '',
  allowExchangeSearch = false,
} = defineProps<{
  show: boolean
  traderCode?: string
  commentGradeId?: string
  exchangeSlug?: string
  feedbackShowType?: string | number
  feedbackLogo?: string
  feedbackName?: string
  allowExchangeSearch?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'example'): void
  (e: 'submit', payload: { description: string; images: string[] }): void
}>()

const appStore = useAppStore()
const route = useRoute()
const { $toast } = useNuxtApp()
const { t, locale } = useAppI18n()
const localePath = useLocalePath()
const dialogMask = useTemplateRef<HTMLElement>('dialogMask')
const { lock: lockBackgroundScroll, unlock: unlockBackgroundScroll } = useStickyDialogScrollLock()
const searchInputWrap = useTemplateRef<HTMLElement>('searchInputWrap')
const searchInput = useTemplateRef<HTMLInputElement>('searchInput')
const fileInput = useTemplateRef<HTMLInputElement>('fileInput')
const description = ref('')
const files = ref<File[]>([])
const imagePreviews = ref<Array<{ url: string; name: string }>>([])
const isDescriptionFocused = ref(false)
const isUploadingImages = ref(false)
const isSubmittingFeedback = ref(false)
const uploadError = ref('')
const waitingForLogin = ref(false)
const isSubmitSuccess = ref(false)
const showGeetest = ref(false)
const hasPassedGeetest = ref(false)
const verifiedFeedbackImages = ref<string[] | null>(null)
const isLoggedIn = computed(() => Boolean(String(appStore.user?.userId ?? '').trim()))
const dialogVisible = computed(() => show && isLoggedIn.value && !appStore.loginShow)
const isSubmitting = computed(() => isUploadingImages.value || isSubmittingFeedback.value || showGeetest.value)
const mediationFeedbackShowType = computed(() => String(feedbackShowType || ''))
const submittedDescription = ref('')
const submittedFeedbackCode = ref('')
const submittedMediationExchangeAlias = ref('')
const submittedMediationLogo = ref('')
const samplePicture = ref<string[]>([])
const previewImageDialogVisible = ref(false)
const previewImageSrc = ref('')

const showCustomTooltip = ref(false)
const arrowLeftOffset = ref(0)

const onTipEnter = (e: MouseEvent) => {
  showCustomTooltip.value = true
  const icon = e.currentTarget as HTMLElement
  const container = icon.closest('.exchange-feedback-dialog-cooperative-content') as HTMLElement
  if (icon && container) {
    const iconRect = icon.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    arrowLeftOffset.value = iconRect.left - containerRect.left + iconRect.width / 2
  }
}

const modalData = ref<Record<string, any> | null>(null)

const {
  searchQuery: searchKeyword,
  searchSuggestList,
  popularTraders,
  hasSearched,
  loadSearchDefault,
  handleSearchInput,
} = useHomeSearch({ immediateDefault: false })
const searchDropdownStyle = ref<Record<string, string>>({})

type SearchBroker = {
  code: string
  logo: string
  name: string
  isOfficial: boolean
  alias?: string
  sortedLabel?: TraderListItem['sortedLabel']
  regulationText?: string
  yearsText?: string
  score?: string | number
}

// 统一把推荐列表和搜索建议映射成弹层可复用的交易所结构。
const mapBrokerToSearchItem = (broker: Partial<TraderListItem>): SearchBroker => {
  return {
    ...broker,
    code: broker.code || '',
    logo: broker.icon || '',
    name: broker.name || '',
    isOfficial: false,
    alias: broker.alias,
    regulationText: broker.status,
    yearsText: broker.years,
    score: broker.score,
  }
}

const searchResults = computed<SearchBroker[]>(() => {
  return searchSuggestList.value.map((broker) => {
    return mapBrokerToSearchItem(broker)
  })
})

const defaultSearchResults = computed<SearchBroker[]>(() => {
  return popularTraders.value.map((broker) => mapBrokerToSearchItem(broker))
})

// 交易所标签展示与头部搜索保持一致，统一从 sortedLabel 中读取状态和年限。
const getExchangeStatus = (broker: SearchBroker) => {
  return broker?.sortedLabel?.find((item) => item?.labelType === 107)
}

const getExchangeYears = (broker: SearchBroker) => {
  return broker?.sortedLabel?.find((item) => item?.labelType === 101)
}

const fixedBroker = computed<SearchBroker | null>(() => {
  if (allowExchangeSearch) {
    return null
  }

  return {
    code: traderCode,
    logo: feedbackLogo,
    name: feedbackName || traderCode,
    isOfficial: false,
  }
})

const isSearchingExchange = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const searchExchange = () => {
  isSearchingExchange.value = !!searchKeyword.value.trim()
  handleSearchInput()

  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    isSearchingExchange.value = false
  }, 1000)
}

const isSearchInputFocused = ref(false)
const selectedBrokerCode = ref('')
const selectedBroker = ref<SearchBroker | null>(null)
const displayBroker = computed(() => selectedBroker.value || fixedBroker.value)
const canClearSelectedBroker = computed(() => allowExchangeSearch && Boolean(selectedBroker.value))
const feedbackTraderCode = computed(() => (allowExchangeSearch ? selectedBrokerCode.value : traderCode))
const mediationExchangeAlias = computed(() => exchangeSlug || selectedBroker.value?.alias)
const hasFeedbackExchange = computed(() => Boolean(feedbackTraderCode.value))
const canSubmit = computed(
  () => hasFeedbackExchange.value && Boolean(description.value.trim()) && files.value.length > 0 && !isSubmitting.value,
)
const hasSearchKeyword = computed(() => searchKeyword.value.trim().length > 0)
const displayedSearchResults = computed<SearchBroker[]>(() => {
  return hasSearchKeyword.value ? searchResults.value : defaultSearchResults.value
})
const showSearchDropdown = computed(() => allowExchangeSearch && !selectedBroker.value && isSearchInputFocused.value)
const showSearchEmpty = computed(() => hasSearchKeyword.value && hasSearched.value && searchResults.value.length === 0)
const showSearchDropdownPanel = computed(
  () => showSearchDropdown.value && (displayedSearchResults.value.length > 0 || showSearchEmpty.value),
)

// 下拉层挂到 body，坐标跟随输入框，避免参与弹窗内部 flex 排版。
const updateSearchDropdownPosition = () => {
  if (!import.meta.client || !showSearchDropdown.value || !searchInputWrap.value) {
    return
  }

  const rect = searchInputWrap.value.getBoundingClientRect()
  searchDropdownStyle.value = {
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    maxHeight: `${Math.max(160, Math.min(476, window.innerHeight - rect.bottom - 16))}px`,
  }
}

// 聚焦后立即更新坐标，避免下拉初次出现时闪到错误位置。
const handleSearchInputFocus = () => {
  isSearchInputFocused.value = true
  if (!hasSearchKeyword.value) {
    void loadSearchDefault()
  }
  updateSearchDropdownPosition()
}

// 输入内容可能改变下拉可见状态，因此同步刷新浮层位置。
const handleSearchKeywordInput = () => {
  searchExchange()
  updateSearchDropdownPosition()
}

const clearSearchKeyword = () => {
  searchKeyword.value = ''
  searchExchange()
  searchInput.value?.focus()
}

// 使用 mousedown 选择，避免输入框 blur 先触发导致点击项丢失。
const selectBroker = (brokerCode: string) => {
  const broker = displayedSearchResults.value.find((item) => item.code === brokerCode)

  if (!broker) {
    return
  }

  selectedBroker.value = broker
  selectedBrokerCode.value = brokerCode
  isSearchInputFocused.value = false
}

// 清除已选交易所，回到搜索输入态继续选择。
const clearSelectedBroker = async () => {
  selectedBroker.value = null
  selectedBrokerCode.value = ''
  searchKeyword.value = ''
  isSearchInputFocused.value = true
  await nextTick()
  searchInput.value?.focus()
}

const highlightMatch = (text: string, query: string) => {
  if (!query || !text) return text
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  return text.replace(regex, '<span class="text-[#E2483D]">$1</span>')
}

watch([displayedSearchResults, showSearchDropdownPanel], async ([_, showDropdown]) => {
  isSearchingExchange.value = false

  if (!showDropdown) {
    searchDropdownStyle.value = {}
    return
  }

  await nextTick()
  updateSearchDropdownPosition()
})

const showMediationSuccess = computed(() => Number(modalData.value?.tipsType) === 0)
const isFeedbackCooperativeShowType = computed(() => modalData?.value?.whiteListType)

const showCooperativeMediationSuccess = computed(
  () => isFeedbackCooperativeShowType.value && showMediationSuccess.value,
)
const modalTitle = computed(() => String(modalData.value?.title || '').trim())
const modalContent = computed(() => String(modalData.value?.content || '').trim())
const modalTipsContent = computed(() =>
  String(modalData.value?.tipsContent || modalData.value?.tipscontent || '').trim(),
)

const descriptionPlaceholder = computed(() => {
  if (isDescriptionFocused.value) {
    return t('83759') + t('83758')
  }
  return t('83758')
})

const textTrafficPrompt = computed(() => {
  const len = description.value.length
  const isZh = ['zh-cn', 'zh', 'zh-tw', 'zh-hk'].includes(locale?.toLowerCase() || '')

  if (isZh) {
    if (len === 0) return t('83767', [`<span style="color: #112D73"> 50 </span>`])
    if (len < 50) return t('83768', [`<span style="color: #112D73"> ${String(50 - len)} </span>`])
    if (len === 3000)
      return t('83769', [
        `<span style="color: #E2483D">${len}</span>`,
        `<span style="color: #E2483D">/</span>`,
        `<span style="color: #E2483D">3000</span>`,
      ])
    return t('83769', [
      `<span style="color: #112D73">${String(len)}</span>`,
      `<span style="color: #112D73">/</span>`,
      `<span style="color: #112D73">3000</span>`,
    ])
  }

  if (len === 0) return t('83767', [`<span style="color: #112D73">100</span>`])
  if (len < 100) return t('83768', [`<span style="color: #112D73">${String(100 - len)}</span>`])
  if (len === 3000) {
    // return t('83769', [
    //   `<span style="color: #E2483D">${len}</span>`,
    //   `<span style="color: #E2483D">/</span>`,
    //   `<span style="color: #E2483D">3000</span>`,
    // ])
    return `<span style="color: #E2483D">${len}</span><span style="color: #E2483D">/</span><span style="color: #E2483D">3000</span>`
  }
  return `<span style="color: #112D73">${String(len)}</span>
    <span style="color: #112D73">/</span>
    <span style="color: #112D73">3000</span>`
  // return t('83769', [
  //   `<span style="color: #112D73">${String(len)}</span>`,
  //   `<span style="color: #112D73">/</span>`,
  //   `<span style="color: #112D73">3000</span>`,
  // ])
})

const imageTrafficPrompt = computed(() => {
  const count = files.value.length
  if (count === 0) {
    return t('83764', [' <span style="color: #112D73">2</span> '])
  }
  if (count === 1) {
    return t('83765', [' <span style="color: #112D73">1</span> '])
  }
  return t('83766', [' <span style="color: #112D73"> ' + String(count) + ' </span> '])
})

const openSubmitSuccess = async () => {
  isSubmitSuccess.value = true
}

const close = () => {
  waitingForLogin.value = false
  isSubmitSuccess.value = false

  resetForm()
  emit('close')
}

// 关闭反馈弹窗并进入申请调解页，同时把上一弹窗内容留给申请页预填。
const goToMediation = async () => {
  const exchangeAlias = submittedMediationExchangeAlias.value || mediationExchangeAlias.value
  const mediationLogo = submittedMediationLogo.value || feedbackLogo

  if (import.meta.client && exchangeAlias) {
    sessionStorage.setItem(`wikibit-mediation-draft:${exchangeAlias}`, submittedDescription.value)
    sessionStorage.setItem(`wikibit-mediation-code:${exchangeAlias}`, submittedFeedbackCode.value)
    sessionStorage.setItem(`wikibit-mediation-showtype:${exchangeAlias}`, mediationFeedbackShowType.value)
    sessionStorage.setItem(
      `wikibit-mediation-contact:${exchangeAlias}`,
      JSON.stringify({
        telphone: getUserProfileText(['phone', 'phoneNumber', 'mobile', 'mobileCode', 'telphone', 'telephone']),
        email: getUserProfileText(['email', 'mail']),
      }),
    )
    sessionStorage.setItem(`wikibit-mediation-logo:${exchangeAlias}`, mediationLogo)
    sessionStorage.setItem(`wikibit-mediation-return:${exchangeAlias}`, route.fullPath)
  }

  isSubmitSuccess.value = false
  emit('close')
  await navigateTo(
    localePath({
      path: '/mediation/apply',
      query: {
        exchange: exchangeAlias,
      },
      external: true,
    }),
  )
}

// 关闭非调解态的提交成功提示，回到调用方页面。
const acknowledgeSubmitSuccess = () => {
  isSubmitSuccess.value = false

  emit('close')
}

const openExample = () => {
  emit('example')
}

const getUserProfileText = (keys: string[]) => {
  const user = appStore.user || {}
  const value = keys
    .map((key) => user[key] ?? appStore[key])
    .find((item) => item !== undefined && item !== null && String(item).trim())

  return value === undefined || value === null ? '' : String(value).trim()
}

const chooseImages = () => {
  if (files.value.length >= 9) {
    return
  }

  fileInput.value?.click()
}

// 解析 webapiupload 返回的图片地址，用于校验本次图片是否全部上传成功。
const extractUploadedImageUrls = (response: unknown) => {
  const responseData = response as {
    wikicommunity?: {
      webapiupload?: {
        data?: Array<{ url?: unknown }>
      }
    }
  }
  const uploadData = responseData.wikicommunity?.webapiupload?.data

  if (!Array.isArray(uploadData)) {
    return []
  }

  return uploadData.map((item) => (typeof item.url === 'string' ? item.url : '')).filter((url) => url.length > 0)
}

// 读取图片文件的 base64 内容，用于匹配 web-wikifxcom 的 webapiupload 入参格式。
const fileToBase64 = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(';base64,')[1] || '')
    })
    reader.addEventListener('error', () => reject(reader.error))
    reader.readAsDataURL(file)
  })
}

// 获取图片后缀，作为 webapiupload payload 的 name 字段。
const resolveImageName = (file: File) => {
  const extension = file.name.split('.').pop()
  return extension || file.type.split('/').pop() || ''
}

// 一次性上传当前已选图片，并返回可提交给反馈接口的图片地址。
const uploadImages = async (selectedFiles: File[]) => {
  if (selectedFiles.length === 0) {
    return []
  }

  uploadError.value = ''
  isUploadingImages.value = true
  try {
    const images = await Promise.all<WebUploadImagePayload>(
      selectedFiles.map(async (file) => ({
        uid: String(appStore.user?.userId || ''),
        base64: await fileToBase64(file),
        name: resolveImageName(file),
        languageCode: appStore.LanguageCode,
        countryCode: appStore.CountryCode,
      })),
    )
    const response = await uploadWebImage(images)
    const uploadedUrls = extractUploadedImageUrls(response)

    if (uploadedUrls.length !== selectedFiles.length) {
      throw new Error('Uploaded image count does not match selected files')
    }

    return uploadedUrls
  } catch {
    // uploadError.value = '图片上传失败，请稍后重试'
    return null
  } finally {
    isUploadingImages.value = false
  }
}

// 将新选择的图片保存在本地，并创建无需网络请求的预览地址。
const handleFiles = (event: Event) => {
  const input = event.currentTarget as HTMLInputElement
  const remainingCount = 9 - files.value.length
  const selectedFiles = Array.from(input.files ?? []).slice(0, remainingCount)
  input.value = ''

  uploadError.value = ''
  hasPassedGeetest.value = false
  verifiedFeedbackImages.value = null
  files.value = [...files.value, ...selectedFiles]
  imagePreviews.value = [
    ...imagePreviews.value,
    ...selectedFiles.map((file) => ({
      url: URL.createObjectURL(file),
      name: file.name,
    })),
  ]
}

// 删除指定本地图片，并同步释放预览地址与上传文件状态。
const removeImage = (index: number) => {
  if (isSubmitting.value) {
    return
  }

  const preview = imagePreviews.value[index]
  if (!preview) {
    return
  }

  URL.revokeObjectURL(preview.url)
  imagePreviews.value.splice(index, 1)
  files.value.splice(index, 1)
  uploadError.value = ''
  hasPassedGeetest.value = false
  verifiedFeedbackImages.value = null
}

// 清空表单状态并释放本地预览地址，用于提交成功后复位弹窗。
const resetForm = () => {
  imagePreviews.value.forEach((image) => URL.revokeObjectURL(image.url))
  description.value = ''
  searchKeyword.value = ''
  selectedBroker.value = null
  selectedBrokerCode.value = ''
  isSearchInputFocused.value = false
  files.value = []
  imagePreviews.value = []
  uploadError.value = ''
  showGeetest.value = false
  hasPassedGeetest.value = false
  verifiedFeedbackImages.value = null
}

// 校验必填项后上传图片，并调用 WikiBit 立即反馈接口提交内容。
const submit = async () => {
  if (!canSubmit.value) {
    return
  }

  uploadError.value = ''
  const uploadedImages =
    hasPassedGeetest.value && verifiedFeedbackImages.value
      ? verifiedFeedbackImages.value
      : await uploadImages(files.value)
  if (!uploadedImages) {
    return
  }

  isSubmittingFeedback.value = true
  try {
    const res = await submitFeedbackImmediate({
      commentGradeId: commentGradeId,
      userId: String(appStore.user?.userId || ''),
      code: feedbackTraderCode.value,
      content: description.value.trim(),
      images: uploadedImages,
      objectType: 0,
    })
    modalData.value = res?.data?.data

    if (modalData.value?.isOpenGeeTest) {
      verifiedFeedbackImages.value = uploadedImages
      hasPassedGeetest.value = false
      showGeetest.value = true
      return
    }

    verifiedFeedbackImages.value = null
    hasPassedGeetest.value = false
    submittedDescription.value = description.value.trim()
    submittedFeedbackCode.value =
      String(
        modalData.value?.code ||
          modalData.value?.commentGradeId ||
          modalData.value?.feedbackCode ||
          modalData.value?.id ||
          '',
      ).trim() || feedbackTraderCode.value
    submittedMediationExchangeAlias.value = mediationExchangeAlias.value
    submittedMediationLogo.value = selectedBroker.value?.logo || feedbackLogo
    $toast('success', t('84421'), { duration: 3000 })
    emit('submit', {
      description: description.value.trim(),
      images: uploadedImages,
    })
    resetForm()
    openSubmitSuccess()
  } catch {
    $toast('error', t('83712'), { duration: 3000 })
  } finally {
    isSubmittingFeedback.value = false
  }
}

const isValidGeetestPayload = (data: Record<string, any>) =>
  Boolean(data?.captcha_id && data?.captcha_output && data?.gen_time && data?.lot_number && data?.pass_token)

// 极验服务端校验通过后，等待用户再次点击立即发布重试当前业务请求。
const sendVCode = async (data: Record<string, any>) => {
  if (!isValidGeetestPayload(data)) {
    showGeetest.value = false
    await nextTick()
    showGeetest.value = true
    return
  }

  const result = await validateFeedbackGeetest({
    lot_number: data.lot_number,
    captcha_output: data.captcha_output,
    pass_token: data.pass_token,
    gen_time: data.gen_time,
    userId: String(appStore.user?.userId || ''),
    code: '',
    type: 2,
    cide: '',
  }).catch(() => null)
  const node = result?.wikicommunity?.validategeettestorcode4
  if (!node?.success) {
    showGeetest.value = false
    hasPassedGeetest.value = false
    $toast('error', t('83712'), { duration: 3000 })
    return
  }

  hasPassedGeetest.value = true
  showGeetest.value = false
}

const closeGeet = () => {
  showGeetest.value = false
  hasPassedGeetest.value = false
  verifiedFeedbackImages.value = null
}

const geetError = () => {
  closeGeet()
  $toast('error', t('83712'), { duration: 3000 })
}

onMounted(async () => {
  if (import.meta.client) {
    window.addEventListener('resize', updateSearchDropdownPosition)
    window.addEventListener('scroll', updateSearchDropdownPosition, true)
  }

  try {
    const data = await getPostFeedbackSetting()
    samplePicture.value = Array.isArray(data?.data?.samplePicture) ? data.data.samplePicture : []
  } catch {}
})

/** 打开示例图片大图预览弹层。 */
const goViewImage = (imageSrc = '') => {
  if (!imageSrc) return

  previewImageSrc.value = imageSrc
  previewImageDialogVisible.value = true
}

watch(
  () => show,
  (value) => {
    if (!import.meta.client) return

    if (!value) {
      waitingForLogin.value = false
      isSubmitSuccess.value = false

      resetForm()
      return
    }

    if (isLoggedIn.value) {
      return
    }

    waitingForLogin.value = true
    appStore.setLoginShow(true)
  },
  { immediate: true },
)

watch(
  () => appStore.loginShow,
  (value) => {
    if (value || !waitingForLogin.value) {
      return
    }

    waitingForLogin.value = false
    if (!isLoggedIn.value) {
      emit('close')
    }
  },
)

watch(
  dialogVisible,
  async (value) => {
    if (!import.meta.client) return

    if (value) {
      lockBackgroundScroll()
      await nextTick()
      dialogMask.value?.focus()
      return
    }

    unlockBackgroundScroll()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  unlockBackgroundScroll()

  if (import.meta.client) {
    window.removeEventListener('resize', updateSearchDropdownPosition)
    window.removeEventListener('scroll', updateSearchDropdownPosition, true)
    imagePreviews.value.forEach((image) => URL.revokeObjectURL(image.url))
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="exchange-feedback-dialog-fade">
      <section
        v-if="dialogVisible"
        ref="dialogMask"
        class="exchange-feedback-dialog-mask"
        tabindex="-1"
        @keydown.esc="close"
      >
        <div
          class="exchange-feedback-dialog"
          :class="{
            'exchange-feedback-dialog--form': !isSubmitSuccess,
            'exchange-feedback-dialog--success': isSubmitSuccess,
            'exchange-feedback-dialog--mediation-success': isSubmitSuccess && showMediationSuccess,
            'exchange-feedback-dialog--ack-success': isSubmitSuccess && !showMediationSuccess,
          }"
          role="dialog"
          aria-modal="true"
          :aria-label="isSubmitSuccess ? 'Feedback completed' : undefined"
          :aria-labelledby="isSubmitSuccess ? undefined : 'exchange-feedback-dialog-title'"
        >
          <div
            v-if="isSubmitSuccess && showMediationSuccess"
            class="relative flex h-full flex-col items-center bg-white px-[32px] pt-[48px] pb-[32px]"
          >
            <button
              type="button"
              class="exchange-feedback-dialog-close absolute top-[28px] right-[28px] rtl:right-auto rtl:left-[28px]"
              aria-label="关闭提交反馈弹窗"
              @click="close"
            >
              <CloseBoldIcon />
            </button>
            <FankuiIcon />

            <template v-if="showCooperativeMediationSuccess">
              <p class="mt-[12px] max-w-[430px] text-center text-[20px] leading-[28px] font-[600] text-[#292A2E]">
                {{ modalTitle }}
              </p>
              <div
                class="exchange-feedback-dialog-cooperative-content relative mt-[20px] w-full max-w-[430px] text-center text-[14px] leading-[22px] font-[400] text-[#7D818A]"
              >
                <span>
                  {{ modalContent }}
                  <span v-if="modalTipsContent" class="exchange-feedback-dialog-tip inline-block">
                    <span
                      class="exchange-feedback-dialog-tip-icon cursor-help transition-colors hover:border-[#112d73] hover:text-[#112d73]"
                      tabindex="0"
                      aria-label="查看说明"
                      @mouseenter="onTipEnter"
                      @mouseleave="showCustomTooltip = false"
                      >?</span
                    >
                  </span>
                </span>

                <div
                  v-show="showCustomTooltip"
                  class="absolute bottom-[calc(100%+5px)] left-0 z-[3020] w-[430px] max-w-[calc(100vw-64px)] rounded-[8px] bg-white p-[16px_20px] text-left text-[14px] leading-[22px] font-[400] text-[#292A2E] shadow-[0_4px_16px_rgba(8,19,62,0.16)]"
                >
                  {{ modalTipsContent }}
                </div>

                <div
                  v-show="showCustomTooltip"
                  class="absolute bottom-[calc(100%-1px)] z-[3021] h-0 w-0 -translate-x-1/2 border-t-[6px] border-r-[6px] border-l-[6px] border-transparent border-t-white drop-shadow-[0_2px_2px_rgba(8,19,62,0.08)]"
                  :style="{ left: arrowLeftOffset + 'px' }"
                ></div>
              </div>
            </template>
            <template v-else>
              <p class="max-w-[430px] py-[12px] text-center text-[16px] leading-[24px] font-[500] text-[#292A2E]">
                {{ modalContent }}
              </p>

              <p
                v-if="modalTipsContent"
                class="max-w-[430px] py-[12px] text-center text-[12px] leading-[18px] font-[400] text-[#7D818A]"
              >
                {{ modalTipsContent }}
              </p>
            </template>

            <button
              type="button"
              class="exchange-feedback-dialog-submit mt-[20px] flex min-w-[184px]"
              @click="goToMediation"
            >
              {{ t('83832') }}
            </button>
          </div>

          <div
            v-else-if="isSubmitSuccess"
            class="flex h-full flex-col items-center bg-white px-[32px] pt-[48px] pb-[32px]"
          >
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/06/26/855d07f5-eec7-4034-8f9c-801e0f4904bf.png"
              class="h-[80px] w-[90px]"
            />

            <p class="max-w-[430px] pt-[16px] text-center text-[20px] leading-[24px] font-[600] text-[#292A2E]">
              {{ modalTitle }}
            </p>
            <p
              v-if="modalTipsContent"
              class="max-w-[430px] pt-[16px] text-center text-[14px] leading-[18px] font-[400] text-[#292A2E]"
            >
              {{ modalTipsContent }}
            </p>

            <button
              type="button"
              class="exchange-feedback-dialog-submit mt-[28px] flex leading-[18px] font-[500]"
              @click="acknowledgeSubmitSuccess"
            >
              {{ t('83585') }}
            </button>
          </div>

          <template v-else>
            <header class="exchange-feedback-dialog-header">
              <div class="exchange-feedback-dialog-title-row">
                <h2 id="exchange-feedback-dialog-title" class="exchange-feedback-dialog-title">{{ t('83756') }}</h2>
                <button
                  type="button"
                  class="exchange-feedback-dialog-close"
                  aria-label="关闭提交反馈弹窗"
                  @click="close"
                >
                  <CloseBoldIcon />
                </button>
              </div>
            </header>

            <div class="exchange-feedback-dialog-body">
              <div class="exchange-feedback-dialog-content">
                <div class="exchange-feedback-dialog-search" v-if="allowExchangeSearch">
                  <label class="exchange-feedback-dialog-search-label" for="exchange-feedback-search-input">
                    {{ t('84354') }} <span class="text-[#E2483D]" aria-hidden="true">*</span>
                  </label>
                  <div v-if="displayBroker" class="exchange-feedback-dialog-search-selected">
                    <div class="exchange-feedback-dialog-search-selected-icon">
                      <CommonImage :src="displayBroker.logo" :alt="displayBroker.name" />
                    </div>
                    <span class="exchange-feedback-dialog-search-selected-name">{{ displayBroker.name }}</span>
                    <button
                      v-if="canClearSelectedBroker"
                      type="button"
                      class="exchange-feedback-dialog-search-selected-clear"
                      aria-label="Clear selected exchange"
                      @click.stop="clearSelectedBroker"
                    >
                      <CommonImage
                        src="https://resources1.interface003.com/web30/2026/06/26/020c0828-d5bd-4293-a244-718628634c54.png"
                        alt="Clear"
                        class="exchange-feedback-dialog-search-selected-clear-icon"
                        aria-hidden="true"
                        loading="eager"
                        object-fit="contain"
                      />
                    </button>
                  </div>
                  <div
                    v-else-if="allowExchangeSearch"
                    ref="searchInputWrap"
                    class="exchange-feedback-dialog-search-input-wrap"
                  >
                    <CommonImage
                      src="https://resources1.interface003.com/web30/2026/05/19/e1e89d8f-6c86-4291-b3a5-df523f7bacf7.svg"
                      alt="Search"
                      class="h-[16px] w-[16px]"
                      loading="eager"
                      object-fit="contain"
                      data-v-6809c4d4=""
                    />
                    <input
                      id="exchange-feedback-search-input"
                      ref="searchInput"
                      v-model="searchKeyword"
                      type="text"
                      :placeholder="$t('84355')"
                      autocomplete="off"
                      class="min-w-0 flex-1 border-none bg-transparent text-[14px] text-[#292A2E] outline-none"
                      @focus="handleSearchInputFocus"
                      @input="handleSearchKeywordInput"
                    />
                    <!-- 清空按钮 -->
                    <CommonImage
                      v-if="searchKeyword && !isSearchingExchange"
                      src="https://resources1.interface003.com/web30/2026/06/26/020c0828-d5bd-4293-a244-718628634c54.png"
                      alt="Clear Search"
                      class="h-[20px] w-[20px] shrink-0 cursor-pointer"
                      loading="eager"
                      object-fit="contain"
                      @click="clearSearchKeyword"
                      @mousedown.prevent
                    />
                    <!-- 加载指示器 -->
                    <svg
                      v-else-if="isSearchingExchange"
                      class="exchange-feedback-dialog-search-spinner"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle cx="12" cy="12" r="10" stroke="#B7B9BE" stroke-width="3" stroke-dasharray="31.4 31.4" />
                    </svg>
                  </div>
                </div>
                <div v-else class="flex items-center gap-[8px]">
                  <div class="h-[32px] w-[32px] rounded-[4px]">
                    <CommonImage
                      :src="displayBroker?.logo || ''"
                      :alt="displayBroker?.name"
                      class="h-[32px] w-[32px] rounded-[4px]"
                    />
                  </div>
                  <span class="text-[18px] font-[500]">{{ displayBroker?.name }}</span>
                </div>
                <label class="exchange-feedback-dialog-label">
                  {{ t('83757') }} <span class="text-[#E2483D]" aria-hidden="true">*</span>
                </label>

                <div class="exchange-feedback-dialog-textarea">
                  <textarea
                    id="exchange-feedback-description"
                    v-model="description"
                    maxlength="3000"
                    :placeholder="descriptionPlaceholder"
                    @blur="isDescriptionFocused = false"
                    @focus="isDescriptionFocused = true"
                  ></textarea>
                  <span v-html="textTrafficPrompt"></span>
                </div>

                <div class="exchange-feedback-dialog-evidence">
                  <div class="exchange-feedback-dialog-evidence-title-row">
                    <h3>
                      {{ t('83760') }} ({{ files.length }}/9) <span class="text-[#E2483D]" aria-hidden="true">*</span>
                    </h3>
                    <el-popover
                      placement="top"
                      trigger="hover"
                      :z-index="3020"
                      width="auto"
                      popper-style="padding: 12px; border-radius: 8px;"
                    >
                      <template #reference>
                        <button type="button">{{ t('83762') }}</button>
                      </template>
                      <!-- TODO: 这里可以放置您的示例图片或文字 -->
                      <div class="flex flex-wrap gap-[8px] text-center text-[14px] text-[#292A2E]">
                        <!-- <CommonImage
                          v-for="value in samplePicture"
                          :src="value"
                          class="h-[108px] w-[108px] rounded-[8px] object-cover"
                          alt=""
                        /> -->
                        <CommonImage
                          v-for="(value, index) in samplePicture"
                          :key="`${value}-${index}`"
                          :src="value"
                          loading="eager"
                          :error-timeout-ms="0"
                          class="!h-[108px] !w-[108px] cursor-pointer rounded-[8px] object-cover transition-opacity duration-200 hover:opacity-80"
                          alt=""
                          @click="goViewImage(value)"
                        />
                      </div>
                    </el-popover>
                  </div>
                  <p>{{ t('84606') }}</p>
                </div>

                <div class="exchange-feedback-dialog-upload-section">
                  <div class="exchange-feedback-dialog-upload-list">
                    <input ref="fileInput" type="file" accept="image/*" multiple hidden @change="handleFiles" />
                    <div v-for="(image, index) in imagePreviews" :key="image.url" class="relative size-[84px] shrink-0">
                      <div
                        class="exchange-feedback-dialog-upload-preview size-full cursor-pointer transition-opacity duration-200 hover:opacity-80"
                        @click="goViewImage(image.url)"
                      >
                        <CommonImage :src="image.url" :alt="image.name" />
                      </div>
                      <button
                        type="button"
                        class="absolute top-[0] right-[0] z-[1] flex size-[16px] cursor-pointer items-center justify-center border-0 bg-transparent p-[0] disabled:cursor-not-allowed disabled:opacity-50 rtl:right-auto rtl:left-[0]"
                        :aria-label="`删除${image.name}`"
                        :disabled="isSubmitting"
                        @click="removeImage(index)"
                      >
                        <CommonImage
                          src="https://resources1.interface003.com/web30/2026/06/22/0af8fb9a-bbca-4496-8e21-2f14cb73f21f.png"
                          alt=""
                          class="size-[16px]"
                        />
                      </button>
                    </div>
                    <button
                      v-if="imagePreviews.length < 9"
                      type="button"
                      class="exchange-feedback-dialog-upload"
                      aria-label="上传图片证明"
                      :aria-busy="isSubmitting"
                      :disabled="isSubmitting"
                      @click="chooseImages"
                    >
                      <CommonImage
                        src="https://resources1.interface003.com/web30/2026/07/07/ea55e549-0793-47ef-9b5d-68f133723efc.svg"
                        alt=""
                        class="size-[24px]"
                      />
                    </button>
                  </div>
                  <p v-if="uploadError">{{ uploadError }}</p>
                  <p v-html="imageTrafficPrompt"></p>
                </div>
              </div>

              <button
                type="button"
                class="exchange-feedback-dialog-submit"
                :aria-busy="isSubmitting"
                :disabled="!canSubmit"
                @click="submit"
              >
                {{ t('83763') }}
              </button>
            </div>
          </template>
        </div>
        <LoginGeetest
          :is-geet="showGeetest"
          :state="1"
          product="bind"
          @geet-path="sendVCode"
          @geet-error="geetError"
          @close-geet="closeGeet"
        />
      </section>
    </Transition>
  </Teleport>
  <Teleport to="body">
    <div
      v-if="dialogVisible && showSearchDropdownPanel"
      class="exchange-feedback-dialog-search-dropdown"
      :style="searchDropdownStyle"
      @mousedown.prevent
    >
      <div v-if="!showSearchEmpty" class="exchange-feedback-dialog-search-dropdown-inner">
        <button
          v-for="broker in displayedSearchResults"
          :key="broker.code"
          type="button"
          class="exchange-feedback-dialog-search-item"
          :class="{
            'exchange-feedback-dialog-search-item--selected': selectedBrokerCode === broker.code,
          }"
          @mousedown.prevent="selectBroker(broker.code)"
        >
          <!-- 图标 -->
          <div class="exchange-feedback-dialog-search-item-icon">
            <CommonImage :src="broker.logo" :alt="broker.name" class="size-full rounded-[4px] object-cover" />
          </div>

          <!-- 信息列 -->
          <div class="exchange-feedback-dialog-search-item-info">
            <span
              class="exchange-feedback-dialog-search-item-name"
              v-html="highlightMatch(broker.name, searchKeyword)"
            ></span>
            <div
              v-if="getExchangeStatus(broker)?.content || getExchangeYears(broker)?.content"
              class="exchange-feedback-dialog-search-item-meta"
            >
              <span
                v-if="getExchangeStatus(broker)?.content"
                :style="{ color: formatColor(getExchangeStatus(broker)?.fontColor) }"
                class="exchange-feedback-dialog-search-item-regulation"
              >
                {{ getExchangeStatus(broker)?.content }}
              </span>
              <span
                v-if="getExchangeStatus(broker)?.content && getExchangeYears(broker)?.content"
                class="exchange-feedback-dialog-search-item-separator"
                aria-hidden="true"
              ></span>
              <span
                v-if="getExchangeYears(broker)?.content"
                :style="{ color: formatColor(getExchangeYears(broker)?.fontColor) }"
                class="exchange-feedback-dialog-search-item-years"
              >
                {{ getExchangeYears(broker)?.content }}
              </span>
            </div>
          </div>

          <!-- 评分 -->
          <div class="exchange-feedback-dialog-search-item-score">
            <StarIcon></StarIcon>
            <span>{{ broker.score }}</span>
          </div>
        </button>
      </div>
      <div v-else class="exchange-feedback-dialog-search-dropdown-empty">
        <div>
          <CommonImage
            class="mx-auto block h-[130px] w-[230px]"
            src="https://resources1.interface003.com/web30/2026/05/26/85c4961e-6d37-43cb-af78-7fbdce783fe5.png"
            alt=""
            loading="eager"
            object-fit="contain"
          />
          <div class="mt-[16px] text-[16px] text-[#292A2E]">{{ t('80359') }}</div>
        </div>
      </div>
    </div>
  </Teleport>
  <CommonImagePreviewDialog v-model="previewImageDialogVisible" :images="previewImageSrc" />
</template>

<style scoped lang="less">
.exchange-feedback-dialog-mask {
  position: fixed;
  z-index: 2000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 20, 51, 0.48);
  padding: 32px;
  outline: none;
}

.exchange-feedback-dialog {
  display: flex;
  height: 600px;
  width: min(540px, calc(100vw - 48px));
  max-height: calc(100vh - 64px);
  flex-direction: column;
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

.exchange-feedback-dialog--form {
  width: min(760px, calc(100vw - 48px));
}

.exchange-feedback-dialog--success {
  min-height: 292px;
  height: auto;
}

.exchange-feedback-dialog--mediation-success {
  height: 488px;
}

.exchange-feedback-dialog-cooperative-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.exchange-feedback-dialog-tip {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}

.exchange-feedback-dialog-tip-icon {
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  border: 1.5px solid #9ca1ad;
  border-radius: 50%;
  color: #9ca1ad;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.exchange-feedback-dialog-tip-icon:focus-visible {
  outline: 2px solid rgba(17, 45, 115, 0.32);
  outline-offset: 2px;
}

.exchange-feedback-dialog-header {
  flex: 0 0 auto;
  background: transparent;
  padding: 32px 32px 0;
}

.exchange-feedback-dialog-title-row {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 8px;
  border-bottom: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  padding-bottom: 16px;
}

.exchange-feedback-dialog-title {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  margin: 0;
  color: var(--color-text, #292a2e);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exchange-feedback-dialog-close {
  display: flex;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  padding: 0;
  transition: transform 0.2s ease;
}

.exchange-feedback-dialog-close:focus-visible,
.exchange-feedback-dialog-upload:focus-visible,
.exchange-feedback-dialog-submit:focus-visible {
  outline: 2px solid rgba(17, 45, 115, 0.32);
  outline-offset: 2px;
}

.exchange-feedback-dialog-close :deep(svg) {
  display: block;
  width: 32px;
  height: 32px;
}

.exchange-feedback-dialog-body {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  overflow-x: hidden;
  background: transparent;
  padding: 20px 32px 32px;
}

.exchange-feedback-dialog-content {
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow-y: auto;
  gap: 16px;

  /* 隐藏滚动条但保留滚动功能 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
    width: 0;
    height: 0;
  }
}

.exchange-feedback-dialog-label,
.exchange-feedback-dialog-evidence-title-row h3 {
  margin: 0;
  color: var(--color-text, #292a2e);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.exchange-feedback-dialog-label span,
.exchange-feedback-dialog-evidence-title-row h3 span {
}

.exchange-feedback-dialog-textarea {
  display: flex;
  height: 132px;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 8px;
  padding: 12px;
}

.exchange-feedback-dialog-textarea:focus-within {
  border-radius: var(--corner-Small, 8px);
  border: 0.5px solid var(--color-border-blue, #4688ec);
}

.exchange-feedback-dialog-textarea textarea {
  width: 100%;
  min-height: 0;
  flex: 1;
  resize: none;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--color-text, #292a2e);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.exchange-feedback-dialog-textarea textarea::placeholder,
.exchange-feedback-dialog-textarea > span {
  color: var(--color-text-disabled, #b7b9be);
}

.exchange-feedback-dialog-textarea > span {
  width: 100%;
  flex: 0 0 auto;
  font-size: 12px;
  line-height: 18px;
  text-align: right;
}

.exchange-feedback-dialog-evidence {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
}

.exchange-feedback-dialog-evidence-title-row {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
}

.exchange-feedback-dialog-evidence-title-row h3 {
  min-width: 0;
  flex: 1;
}

.exchange-feedback-dialog-evidence-title-row button {
  cursor: pointer;
  border: 0;
  background: transparent;
  color: var(--color-link-default, #0d3f63);
  font-size: 14px;
  line-height: 22px;
  padding: 0;
  white-space: nowrap;
}

.exchange-feedback-dialog-evidence > p,
.exchange-feedback-dialog-upload-section > p {
  width: 100%;
  margin: 0;
  color: var(--color-text-subtlest, #7d818a);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.exchange-feedback-dialog-upload-section {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
}

.exchange-feedback-dialog-upload-list {
  display: flex;
  min-height: 84px;
  width: 100%;
  align-items: center;
  gap: 13px;
  flex-wrap: wrap;
}

.exchange-feedback-dialog-upload-preview,
.exchange-feedback-dialog-upload {
  display: flex;
  width: 84px;
  height: 84px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 8px;
  background: var(--color-background-subtlest, #f8f8f8);
  color: #7d818a;
  padding: 0;
  border: 1px solid transparent;
  background:
    linear-gradient(#f8f8f8, #f8f8f8) padding-box,
    repeating-linear-gradient(90deg, rgba(11, 18, 40, 0.14) 0 7px, transparent 7px 14px) top / 100% 1px no-repeat
      border-box,
    repeating-linear-gradient(90deg, rgba(11, 18, 40, 0.14) 0 7px, transparent 7px 14px) bottom / 100% 1px no-repeat
      border-box,
    repeating-linear-gradient(180deg, rgba(11, 18, 40, 0.14) 0 7px, transparent 7px 14px) left / 1px 100% no-repeat
      border-box,
    repeating-linear-gradient(180deg, rgba(11, 18, 40, 0.14) 0 7px, transparent 7px 14px) right / 1px 100% no-repeat
      border-box;
}

.exchange-feedback-dialog-upload-preview {
  overflow: hidden;
  border: 1px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  background: #f8f8f8;
}

.exchange-feedback-dialog-upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.exchange-feedback-dialog-upload svg {
  width: 24px;
  height: 24px;
}

.exchange-feedback-dialog-upload-section > p {
  color: var(--color-text, #292a2e);
}

.exchange-feedback-dialog-upload-section strong {
  color: #112d73;
  font-weight: 400;
}

.exchange-feedback-dialog-submit {
  display: flex;
  min-width: 184px;
  min-height: 46px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 100px;
  background: var(--color-brand-default, #112d73);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  padding: 12px 64px;
  white-space: nowrap;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.exchange-feedback-dialog-submit:hover {
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
}

.exchange-feedback-dialog-submit:active {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%), #112d73;
}

.exchange-feedback-dialog-submit:disabled {
  cursor: not-allowed;
  background: #afc5fa;
  transform: none;
}

.exchange-feedback-dialog-fade-enter-active,
.exchange-feedback-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.exchange-feedback-dialog-fade-enter-active .exchange-feedback-dialog,
.exchange-feedback-dialog-fade-leave-active .exchange-feedback-dialog {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.exchange-feedback-dialog-fade-enter-from,
.exchange-feedback-dialog-fade-leave-to {
  opacity: 0;
}

.exchange-feedback-dialog-fade-enter-from .exchange-feedback-dialog,
.exchange-feedback-dialog-fade-leave-to .exchange-feedback-dialog {
  opacity: 0;
}
.exchange-feedback-dialog-search {
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
}

.exchange-feedback-dialog-search-label {
  margin: 0;
  color: var(--color-text, #292a2e);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;

  span {
    color: #e2483d;
  }
}

.exchange-feedback-dialog-search-input-wrap {
  position: relative;
  display: flex;
  width: 100%;
  height: 46px;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 8px;
  padding: 12px 16px;
}

.exchange-feedback-dialog-search-selected {
  display: flex;
  width: 100%;
  height: 46px;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
  overflow: hidden;
  border: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  border-radius: 8px;
  padding: 12px 16px;
}

.exchange-feedback-dialog-search-selected-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 2px;
}

.exchange-feedback-dialog-search-selected-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.exchange-feedback-dialog-search-selected-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exchange-feedback-dialog-search-selected-clear {
  display: flex;
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #7d818a;
  opacity: 0;
  padding: 0;
  pointer-events: none;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    opacity 0.15s ease;
}

.exchange-feedback-dialog-search-selected:hover .exchange-feedback-dialog-search-selected-clear,
.exchange-feedback-dialog-search-selected-clear:focus-visible {
  opacity: 1;
  pointer-events: auto;
}

.exchange-feedback-dialog-search-selected-clear:hover {
  background: #f8f8f8;
  color: #292a2e;
}

.exchange-feedback-dialog-search-selected-clear-icon {
  width: 20px;
  height: 20px;
}

.exchange-feedback-dialog-search-spinner {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  animation: exchange-feedback-dialog-spin 0.8s linear infinite;
}

@keyframes exchange-feedback-dialog-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== 搜索结果下拉 ========== */
.exchange-feedback-dialog-search-dropdown {
  position: fixed;
  z-index: 3025;
  max-height: 476px;
  overflow: hidden;
  border: 1px solid rgba(5, 21, 36, 0.06);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(8, 19, 62, 0.12);
}

.exchange-feedback-dialog-search-dropdown-inner {
  max-height: 476px;
  overflow-y: auto;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.exchange-feedback-dialog-search-item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 12px 32px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: #f8f8f8;
  }

  &--selected {
    background: rgba(17, 45, 115, 0.06);
  }
}

.exchange-feedback-dialog-search-item-icon {
  display: flex;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
  background: #f8f8f8;
}

.exchange-feedback-dialog-search-item-info {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.exchange-feedback-dialog-search-item-name {
  display: flex;
  align-items: center;
  color: #292a2e;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exchange-feedback-dialog-search-item-official {
  display: inline-flex;
  align-items: center;
  height: 18px;
  flex: 0 0 auto;
  border-radius: 2px;
  background: #357de8;
  padding: 0 4px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
}

.exchange-feedback-dialog-search-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.exchange-feedback-dialog-search-item-regulation {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
}

.exchange-feedback-dialog-search-item-separator {
  display: block;
  width: 0;
  height: 8px;
  flex: 0 0 auto;
  border-left: 0.5px solid rgba(5, 21, 36, 0.12);
}

.exchange-feedback-dialog-search-item-years {
  color: #505258;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
}

.exchange-feedback-dialog-search-item-score {
  display: flex;
  height: 32px;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  border-radius: 1000px;
  background: #f8f8f8;
  padding: 4px 12px;
  color: #292a2e;
  font-family: WikiFX;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  white-space: nowrap;
}

.exchange-feedback-dialog-search-dropdown-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #b7b9be;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-align: center;
}

@media screen and (max-width: 799px) {
  .exchange-feedback-dialog-mask {
    padding: 16px;
  }

  .exchange-feedback-dialog {
    height: auto;
    width: 100%;
    border-radius: 20px;
  }

  .exchange-feedback-dialog--mediation-success {
    min-height: 488px;
  }

  .exchange-feedback-dialog-tip-popover {
    width: 280px;
    max-width: calc(100vw - 64px);
  }

  .exchange-feedback-dialog-header {
    padding: 24px 24px 0;
  }

  .exchange-feedback-dialog-body {
    padding: 20px 24px 24px;
  }
}
</style>

<style lang="less">
.exchange-feedback-tooltip.el-popper {
  z-index: 3020 !important;
  --el-popper-theme-background: #ffffff !important;
  --el-popper-theme-background-light: #ffffff !important;
  width: 430px !important;
  max-width: calc(100vw - 64px) !important;
  padding: 16px 20px !important;
  border: none !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  box-shadow: 0 4px 16px rgba(8, 19, 62, 0.16) !important;
  color: #292a2e !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 22px !important;

  .el-popper__arrow::before {
    border: none !important;
    background: #ffffff !important;
    box-shadow: 4px 4px 8px rgba(8, 19, 62, 0.08) !important;
  }
}
</style>
